import React, { useEffect, useState } from "react";

import { NotificationCard, RHFTextField, FormField } from "@arp/react-ndbx";

//** MUI Components */
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { FormHelperText } from "@mui/material";

import { useTheme } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";

//----------------------------------------------------------------

const RHFAutocomplete = React.forwardRef((props, ref) => (
	<FormField ref={ref} Component={RHFAutocompleteImp} {...props} />
));

const RHFAutocompleteImp = React.forwardRef(
	(
		{
			name = "undefined",
			defaultValue = "",
			accessToken,
			func,
			functionType = "mutation",
			label,
			labelOption,
			noOptionTest = "Start typing Now...",
			variant = "standard",
			size,
			onChange, // We use this to manually update the FormProvider field
			error,
			helperText,
			showErrorCard = true,
			extendedError = { title: null, additionalText: null },
			freeSolo = true,
		},
		ref
	) => {
		const useStyles = makeStyles()((theme) => {
			return {
				root: {
					marginLeft: "auto",
					fontSize: 10,
					color: theme.palette.greyscale.Grey500,
					marginTop: 0.5,
					marginBottom: 1,
				},
			};
		});

		const theme = useTheme();
		const { classes } = useStyles();
		// const [value, setValue] = useState(null);
		const [inputValue, setInputValue] = useState(null);
		const [options, setOptions] = useState([]);

		const { mutate, data, refetch, isFetching, isLoading, fetchStatus } = func({
			locality: inputValue,
			accessToken: accessToken,
		});

		const isLoadingV3 = isLoading && fetchStatus !== "idle";

		useEffect(() => {
			if (data == null || isLoading === true) return;
			setOptions(data);
		}, [data]);

		const handleInputChange = (event, newInputValue) => {
			if (event == null) return; //* Only fetch data if we are entering manually.
			event?.preventDefault();
			setInputValue(newInputValue);
			if (newInputValue !== "") {
				if (functionType === "mutation") {
					mutate({ newInputValue, accessToken });
				} else {
					refetch({ locality: newInputValue, accessToken: accessToken });
				}
			} else {
				setOptions([]);
			}
		};

		return (
			<FormControl sx={{ width: "100%" }} size={size}>
				<Autocomplete
					id={`${name}-autocomplete`}
					name={name}
					fullWidth
					getOptionLabel={(option) =>
						typeof option === "string" ? option : option[labelOption]
					}
					error={error}
					defaultValue={defaultValue || null}
					filterOptions={(x) => x}
					options={options}
					autoComplete
					freeSolo={freeSolo}
					disableClearable
					includeInputInList
					filterSelectedOptions
					noOptionsText={noOptionTest}
					onChange={(event, newValue) => {
						// setValue(newValue);
						onChange(newValue);
					}}
					onInputChange={handleInputChange}
					renderOption={(props, option) => (
						<li {...props} key={option.id}>
							{option[labelOption]}
						</li>
					)}
					renderInput={(params) => (
						<RHFTextField
							{...params}
							variant={variant}
							name={name}
							label={label}
							error={error}
							fullWidth
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{isLoadingV3 && (
											<CircularProgress color="inherit" size={20} />
										)}
										{params.InputProps.endAdornment}
									</>
								),
								autoComplete: "disabled", //
							}}
							inputRef={ref}
							helperText={params?.inputProps?.placeholder?.toUpperCase()}
							FormHelperTextProps={{
								classes: {
									root: classes.root,
								},
							}}
						/>
					)}
				/>
				{helperText && showErrorCard === true && (
					<NotificationCard
						sx={{ mt: 1 }}
						message={helperText}
						{...extendedError}
					/>
				)}
				{helperText && showErrorCard === false && (
					<FormHelperText sx={{ color: theme.palette.signal.Error600 }}>
						{helperText}
					</FormHelperText>
				)}
			</FormControl>
		);
	}
);
export default RHFAutocomplete;
